const STALE_ACTION_DOCS_SLUG = 'failed-to-find-server-action';
const STALE_ACTION_DOCS_PATH = '/docs/messages/failed-to-find-server-action';
const STALE_ACTION_DOCS_URL =
  'https://nextjs.org/docs/messages/failed-to-find-server-action';
const STALE_ACTION_LEGACY_MESSAGE = `Failed to find Server Action. Read more: ${STALE_ACTION_DOCS_URL}`;
const STALE_ACTION_NAME = 'UnrecognizedActionError';
const STALE_ACTION_CODE = 'E715';
const MALFORMED_SERVER_REFERENCE_MESSAGE =
  'The Server Reference ID did not match the expected format.';
const AMBIGUOUS_DEPLOYMENT_MESSAGE =
  'This request might be from an older or newer deployment.';
// Only a quoted action id may separate the subject from the predicate. Allowing
// arbitrary text here could suppress an error about a different missing value.
const STALE_ACTION_WITHOUT_ID_MESSAGE =
  /\bserver\s+action\s+was\s+not\s+found\s+on\s+the\s+server\b/i;
const STALE_ACTION_STACK_FRAME =
  /(?:^|\n)\s*at\s+[^\r\n]*\/docs\/messages\/failed-to-find-server-action(?:[)\s:]|$)/;
const VALID_STALE_ACTION_ID = /^[0-9a-f]{42}$/i;

type ErrorStringProperty = '__NEXT_ERROR_CODE' | 'message' | 'name' | 'stack';

export type ServerActionErrorClassification = 'other' | 'retained' | 'stale';

type QuotedActionIdEvidence = 'absent' | 'malformed' | 'valid';

type StringPropertySnapshot = Readonly<{
  isMalformed: boolean;
  value: string | null;
}>;

type ServerActionErrorSnapshot =
  | Readonly<{ kind: 'string'; text: string }>
  | Readonly<{
      code: string | null;
      isMalformed: boolean;
      kind: 'object';
      message: string | null;
      name: string | null;
      stack: string | null;
    }>
  | Readonly<{ kind: 'unsupported' }>;

function snapshotStringProperty(
  reason: object,
  property: ErrorStringProperty,
): StringPropertySnapshot {
  try {
    const value = Reflect.get(reason, property);
    if (value === null || value === undefined) {
      return { isMalformed: false, value: null };
    }
    if (typeof value !== 'string') {
      return { isMalformed: true, value: null };
    }
    return {
      isMalformed: false,
      value,
    };
  } catch {
    return { isMalformed: true, value: null };
  }
}

/** Read each dynamic field once before applying any classification rule. */
function snapshotServerActionError(reason: unknown): ServerActionErrorSnapshot {
  if (typeof reason === 'string') {
    return Object.freeze({ kind: 'string' as const, text: reason });
  }
  if (reason === null || typeof reason !== 'object') {
    return Object.freeze({ kind: 'unsupported' as const });
  }

  const code = snapshotStringProperty(reason, '__NEXT_ERROR_CODE');
  const message = snapshotStringProperty(reason, 'message');
  const name = snapshotStringProperty(reason, 'name');
  const stack = snapshotStringProperty(reason, 'stack');

  return Object.freeze({
    code: code.value,
    isMalformed:
      code.isMalformed ||
      message.isMalformed ||
      name.isMalformed ||
      stack.isMalformed,
    kind: 'object' as const,
    message: message.value,
    name: name.value,
    stack: stack.value,
  });
}

function isRetainedNextErrorCode(code: string | null): boolean {
  return code === 'E1442' || code === 'E975';
}

/**
 * Parses only the observed quoted sentence, one physical line at a time.
 *
 * The first subject and final fixed suffix on each line bound the token, making
 * nested subjects and earlier suffix-shaped text ordinary token content.
 * Bounded linear passes remain safe for attacker-supplied, very long action ids.
 */
function classifyQuotedActionIdEvidence(text: string): QuotedActionIdEvidence {
  const subjectPattern = /\bserver action "/i;
  const predicatePattern = /" was not found on the server/gi;
  let hasValidId = false;
  let lineStart = 0;

  for (let lineEnd = 0; lineEnd <= text.length; lineEnd += 1) {
    const character = text.charCodeAt(lineEnd);
    const isTextEnd = lineEnd === text.length;
    if (!isTextEnd && character !== 10 && character !== 13) continue;

    const line = text.slice(lineStart, lineEnd);
    const subjectMatch = subjectPattern.exec(line);
    if (subjectMatch !== null) {
      const actionIdStart = subjectMatch.index + subjectMatch[0].length;
      const lineRemainder = line.slice(actionIdStart);
      predicatePattern.lastIndex = 0;
      let predicateMatch = predicatePattern.exec(lineRemainder);
      let finalPredicateMatch: RegExpExecArray | null = null;
      while (predicateMatch !== null) {
        finalPredicateMatch = predicateMatch;
        predicateMatch = predicatePattern.exec(lineRemainder);
      }

      if (finalPredicateMatch !== null) {
        const actionId = lineRemainder.slice(0, finalPredicateMatch.index);
        if (!VALID_STALE_ACTION_ID.test(actionId)) return 'malformed';
        hasValidId = true;
      }
    }

    if (isTextEnd) break;
    if (character === 13 && text.charCodeAt(lineEnd + 1) === 10) lineEnd += 1;
    lineStart = lineEnd + 1;
  }

  return hasValidId ? 'valid' : 'absent';
}

function containsRetainedActionSignal(text: string): boolean {
  return (
    text.includes(MALFORMED_SERVER_REFERENCE_MESSAGE) ||
    text.includes(AMBIGUOUS_DEPLOYMENT_MESSAGE) ||
    classifyQuotedActionIdEvidence(text) === 'malformed'
  );
}

function containsStaleActionSignature(text: string): boolean {
  return (
    text === STALE_ACTION_DOCS_SLUG ||
    text === STALE_ACTION_DOCS_PATH ||
    text === STALE_ACTION_DOCS_URL ||
    text === STALE_ACTION_LEGACY_MESSAGE ||
    STALE_ACTION_WITHOUT_ID_MESSAGE.test(text) ||
    classifyQuotedActionIdEvidence(text) === 'valid' ||
    STALE_ACTION_STACK_FRAME.test(text)
  );
}

function hasRetainedSignal(snapshot: ServerActionErrorSnapshot): boolean {
  if (snapshot.kind === 'unsupported') return false;
  if (snapshot.kind === 'string') {
    return containsRetainedActionSignal(snapshot.text);
  }
  if (snapshot.isMalformed) return true;

  return (
    isRetainedNextErrorCode(snapshot.code) ||
    (snapshot.message !== null &&
      containsRetainedActionSignal(snapshot.message)) ||
    (snapshot.stack !== null && containsRetainedActionSignal(snapshot.stack))
  );
}

function hasStaleSignal(snapshot: ServerActionErrorSnapshot): boolean {
  if (snapshot.kind === 'unsupported') return false;
  if (snapshot.kind === 'string') {
    return containsStaleActionSignature(snapshot.text);
  }

  if (snapshot.code === STALE_ACTION_CODE) return true;
  if (snapshot.name === STALE_ACTION_NAME) return true;
  if (
    snapshot.message !== null &&
    containsStaleActionSignature(snapshot.message)
  ) {
    return true;
  }
  return (
    snapshot.stack !== null && containsStaleActionSignature(snapshot.stack)
  );
}

function classifySnapshot(
  snapshot: ServerActionErrorSnapshot,
): ServerActionErrorClassification {
  if (hasRetainedSignal(snapshot)) return 'retained';
  if (hasStaleSignal(snapshot)) return 'stale';
  return 'other';
}

/**
 * Classifies one immutable snapshot so hostile or changing accessors cannot
 * produce contradictory retained and stale decisions.
 */
export function classifyServerActionError(
  reason: unknown,
): ServerActionErrorClassification {
  return classifySnapshot(snapshotServerActionError(reason));
}

/**
 * Identifies malformed or ambiguous Server Action failures that must remain
 * visible in telemetry even when another field looks like a stale action.
 */
export function isRetainedServerActionError(reason: unknown): boolean {
  return classifyServerActionError(reason) === 'retained';
}

/**
 * Identifies the framework failure emitted when a client calls a Server Action
 * id that the active deployment no longer serves.
 *
 * This module intentionally has no server-only dependencies so browser code
 * can reuse the same conservative classification.
 */
export function isStaleServerActionError(reason: unknown): boolean {
  return classifyServerActionError(reason) === 'stale';
}

/**
 * Event carrying a Server Action failure that its caller already HANDLED.
 *
 * `StaleDeploymentNotice` learns about failures from `unhandledrejection` and
 * `window.onerror`, which only ever see the ones nobody caught. A poll loop
 * that dutifully catches its own errors is therefore invisible to it — so when
 * Vercel starts answering a pinned deployment's Server Action POSTs with 404,
 * the loop reads it as a transport blip and retries forever against a
 * deployment that will never answer. RELM-370 is that: two E394s two minutes
 * apart while the page said "Retrying…" and the research never updated.
 */
export const HANDLED_SERVER_ACTION_FAILURE_EVENT =
  'relm:handled-server-action-failure';

/**
 * Offer a handled failure to the stale-deployment detector.
 *
 * Fire-and-forget and side-effect free for every ordinary error: the detector
 * applies the same deny-first classification it applies to uncaught ones, and a
 * failure that is merely consistent with skew still has to survive the
 * `deploymentStillServed()` probe before anything is shown. Callers keep their
 * own handling — this only stops a genuine skew 404 from being retried in
 * silence.
 */
export function reportHandledServerActionFailure(reason: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(HANDLED_SERVER_ACTION_FAILURE_EVENT, { detail: reason }),
  );
}
